import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig";
import { useParams } from "react-router-dom";
import { Container, Paper, Typography, TextField, FormControlLabel, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Select, MenuItem } from "@mui/material";
import { getCateringLabel, getOvernightLabel } from "../utils/labels";
import { useAuth } from "../context/AuthContext";

import { handleApiError } from "../utils/apiErrorHandler";
import { useRegistrationStatus } from '../hooks/useRegistrationStatus';
import { toast } from 'react-toastify';

/*const getCateringLabel = (key: string) => {
    const labels: Record<string, string> = {
      dinnerFriday: "Abendessen Freitag",
      dinnerFridayVeg: "Abendessen Freitag (Vegetarisch)",
      breakfastSaturday: "Frühstück Samstag",
      breakfastSunday: "Frühstück Sonntag"
    };
    return labels[key] || key; // Falls ein neuer Key hinzugefügt wird, einfach den Original-Key zurückgeben
  };
  */
const OtherBookingsPage: React.FC = () => {
    const { competitionId } = useParams(); // IDs aus der URL erhalten
    const [loading, setLoading] = useState(true);
    const [competition, setCompetition] = useState<any>(null); // Wettkampf-Daten speichern
    const [tShirtBooking, setTShirtBooking] = useState({
      individualizable: false,
      customText: "",
      sizes: {
        "128": 0, "140": 0, "152": 0, "164": 0,
        "Damen S": 0, "Damen M": 0, "Damen L": 0, "Damen XL": 0,
        "Herren S": 0, "Herren M": 0, "Herren L": 0, "Herren XL": 0, "Herren XXL": 0
      }
    });
    const TSHIRT_SIZES = ["128", "140", "152", "164", "Damen S", "Damen M", "Damen L", "Damen XL", "Herren S", "Herren M", "Herren L", "Herren XL", "Herren XXL"] as const;
    const availableCateringOptions = Object.entries(competition?.catering || {})
    .filter(([_, details]) => (details as { available: boolean })?.available) // ✅ `?.` verhindert Fehler
    .map(([key, details]) => ({
        key,
        label: getCateringLabel(key),
        price: (details as { price: number })?.price || 0, // ✅ Falls `price` fehlt, auf 0 setzen
    }));

    const availableOvernightOptions = Object.entries(competition?.overnight || {})
    .filter(([_, details]) => (details as { available: boolean })?.available)
    .map(([key, details]) => ({
        key,
        label: getOvernightLabel(key),
        price: (details as { price: number })?.price || 0,
    }));

    const getTShirtPrice = () => {
      if (!competition?.tShirt) return 0; // Falls Preise nicht geladen sind
      return tShirtBooking.individualizable
        ? competition.tShirt.priceIndividualized
        : competition.tShirt.priceStandard;
    }

    const [cateringBooking, setCateringBooking] = useState({
      dinnerFriday: 0, dinnerFridayVeg: 0, breakfastSaturday: 0, breakfastSunday: 0
    });

    const [overnightBooking, setOvernightBooking] = useState({
      fridayToSaturday: 0, saturdayToSunday: 0
    });

    const clubId = useAuth().clubId;

    const registrationStatus = useRegistrationStatus(competitionId);

    useEffect(() => {
      
      const fetchData = async () => {
        try {
          const competitionResponse = await axios.get(`/api/competitions/${competitionId}`);
          setCompetition(competitionResponse.data);
  
          // Falls es eine vorhandene Buchung gibt, laden          
          const preBookingResponse = await axios.get(`/api/pre-registrations/${competitionId}/${clubId}`);
          if (preBookingResponse.data) {
            setTShirtBooking(preBookingResponse.data.tShirtBooking || tShirtBooking);
            setCateringBooking(preBookingResponse.data.cateringBooking || cateringBooking);
            setOvernightBooking(preBookingResponse.data.overnightBooking || overnightBooking);
          }
        } catch (error) {
          handleApiError(error, "Fehler beim Laden der Daten.");
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, [competitionId, clubId, useAuth]);
  
    return (
        <Container component="main" maxWidth="md">
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Buchung von Zusatzleistungen</Typography>
    
            {loading ? (
              <Typography>Lade Daten...</Typography>
            ) : (
              <>
                {/* T-Shirt Buchung */}
                {competition?.tShirt.available && (
                  <>
                    <Typography variant="h6">👕 T-Shirt Bestellung</Typography>
                    <FormControlLabel
                      control={<Checkbox checked={tShirtBooking.individualizable} onChange={(e) => setTShirtBooking({ ...tShirtBooking, individualizable: e.target.checked })} />}
                      label="T-Shirts mit individuellem Vereinsnamen"
                      disabled={registrationStatus.isClosed}
                    />
                    {tShirtBooking.individualizable && (
                      <TextField
                        fullWidth
                        label="Vereinsname"
                        value={tShirtBooking.customText}
                        onChange={(e) => setTShirtBooking({ ...tShirtBooking, customText: e.target.value })}
                        sx={{ my: 2 }}
                        disabled={registrationStatus.isClosed}
                      />
                    )}
    
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Größe</TableCell>
                            <TableCell>Preis pro Stück</TableCell>
                            <TableCell>Anzahl</TableCell>
                            <TableCell>Gesamtpreis</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                        {TSHIRT_SIZES.map((size) => (
                            <TableRow key={size}>
                              <TableCell>{size}</TableCell>
                              <TableCell>{getTShirtPrice().toFixed(2)} €</TableCell>
                              <TableCell>
                              <TextField
                                    type="number"
                                    value={tShirtBooking.sizes[size]} 
                                    onChange={(e) => setTShirtBooking({
                                    ...tShirtBooking,
                                    sizes: { 
                                        ...tShirtBooking.sizes, 
                                        [size]: parseInt(e.target.value) || 0 
                                    }
                                    })}
                                    inputProps={{ min: 0 }}
                                    sx={{ width: 80 }}
                                    disabled={registrationStatus.isClosed}
                                />
                              </TableCell>
                              <TableCell>
                              <strong>{(tShirtBooking.sizes[size] * getTShirtPrice()).toFixed(2)} €</strong>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
    
                {/* Verpflegung Buchung */}
                {availableCateringOptions.length > 0 && (
                <>
                    <Typography variant="h6" sx={{ mt: 3 }}>🍽️ Verpflegung</Typography>
                    <TableContainer>
                    <Table size="small">
                        <TableHead>
                        <TableRow>
                            <TableCell>Option</TableCell>
                            <TableCell>Preis pro Essen</TableCell>
                            <TableCell>Anzahl</TableCell>
                            <TableCell>Gesamtpreis</TableCell>
                        </TableRow>
                        </TableHead>
                        <TableBody>
                        {availableCateringOptions.map(({ key, label, price }) => {
                          const quantity = cateringBooking[key as keyof typeof cateringBooking] || 0;
                          const totalPrice = quantity * price; // 🔹 Berechnung des Gesamtpreises

                          return (
                            <TableRow key={key}>
                              <TableCell>{label}</TableCell>
                              <TableCell>{price.toFixed(2)} €</TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  value={quantity}
                                  onChange={(e) =>
                                    setCateringBooking((prev) => ({
                                      ...prev,
                                      [key]: parseInt(e.target.value) || 0,
                                    }))
                                  }
                                  inputProps={{ min: 0 }}
                                  sx={{ width: 80 }}disabled={registrationStatus.isClosed}
                                />
                              </TableCell>
                              <TableCell><strong>{totalPrice.toFixed(2)} €</strong></TableCell> 
                            </TableRow>
                          );
                        })}
                      </TableBody>

                    </Table>
                    </TableContainer>
                </>
                )}

                {/* Übernachtung Buchung */}
                {availableOvernightOptions.length > 0 && (
                <>
                    <Typography variant="h6" sx={{ mt: 3 }}>🌙 Übernachtung</Typography>
                    <TableContainer>
                    <Table size="small">
                        <TableHead>
                        <TableRow>
                            <TableCell>Option</TableCell>
                            <TableCell>Preis pro Person</TableCell>
                            <TableCell>Anzahl Personen</TableCell>
                            <TableCell>Gesamtpreis</TableCell>
                        </TableRow>
                        </TableHead>
                        <TableBody>
                        {availableOvernightOptions.map(({ key, label, price }) => {
                          const quantity = overnightBooking[key as keyof typeof overnightBooking] || 0;
                          const totalPrice = quantity * price;

                          return (
                            <TableRow key={key}>
                              <TableCell>{label}</TableCell>
                              <TableCell>{price.toFixed(2)} €</TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  value={quantity}
                                  onChange={(e) =>
                                    setOvernightBooking((prev) => ({
                                      ...prev,
                                      [key]: parseInt(e.target.value) || 0,
                                    }))
                                  }
                                  inputProps={{ min: 0 }}
                                  sx={{ width: 80 }}
                                  disabled={registrationStatus.isClosed}
                                />
                              </TableCell>
                              <TableCell><strong>{totalPrice.toFixed(2)} €</strong></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>

                    </Table>
                    </TableContainer>
                </>
                )}

                {/* Speichern */}
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 3 }}
                  onClick={async () => {
                    if (registrationStatus.isClosed) {
                      toast.error('Die Meldung ist geschlossen. Änderungen sind nicht mehr möglich.');
                      return;
                    }
                    try {
                      await axios.put(`/api/pre-registrations/${competitionId}/${clubId}`, {
                        tShirtBooking,
                        cateringBooking,
                        overnightBooking
                      });
                      alert("Buchung gespeichert!");
                    } catch (error) {
                      handleApiError(error, "Fehler beim Speichern");
                    }
                  }}
                >
                  {registrationStatus.isClosed ? 'Meldung geschlossen' : 'Speichern'}
                </Button>
              </>
            )}
          </Paper>
        </Container>
      );
    };
    
    export default OtherBookingsPage;
    